import { Divider, FormGroup, Radio, RadioGroup } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { ICustomControlDiagnosticReportAddEditProps } from '../../interfaces/';
import { ConclusieDiagnoseOption } from '../../../../../../common/enums/conclusieDiagnoseOption';
import { nullDiagnosis } from '../../../../../../common/validation/diagnosis';
import { Feedback, InputRadioGroup } from '../../../../elements/common';
import { Icd10Selection } from '../../utils/v1/Icd10/Icd10Selection';
import { orthCV1DiagnosticReportWerkdiagnoseLabels } from './OrthCV1DiagnosticReportRead';

interface IOrthCV1DiagnosticReportAddEditWerkdiagnoseProps extends ICustomControlDiagnosticReportAddEditProps {
    resetWerkdiagnose: () => void;
}

const Icd10Werkdiagnose: { [key: string]: string } = {
    hypermetropie: 'H52.0',
    myopie: 'H52.1',
    anisometropie: 'H52.3',
    astigmatisme: 'H52.2',
    insufficientie_en_overmatige_convergentie: 'H51.1',
    strabismus: 'H50.9',
    amblyopie: 'H53.0',
};

export function OrthCV1DiagnosticReportAddEditWerkdiagnose(props: IOrthCV1DiagnosticReportAddEditWerkdiagnoseProps) {
    const [hasWerkdiagnoseError, setHasWerkdiagnoseError] = useState(false);

    useEffect(() => {
        setHasWerkdiagnoseError(
            props.errors.has('customControlData.werkdiagnose') ||
                props.errors.has('diagnosis.diagnosisConsiderationIcd10')
        );
    }, [props.errors]);

    useEffect(() => {
        props.validateOneCustomControl('werkdiagnose');
        props.validateOne('diagnosis');
    }, [props.diagnosticReport.customControlData.werkdiagnose]);

    useEffect(() => {
        if (
            props.diagnosticReport.customControlData.conclusie_diagnose &&
            props.diagnosticReport.customControlData.conclusie_diagnose !== ConclusieDiagnoseOption.ja
        ) {
            props.validateOneCustomControl('conclusie_diagnose');
        }
    }, [props.diagnosticReport.customControlData.conclusie_diagnose]);

    return (
        <>
            <FormGroup
                hasErrors={props.errors.has('customControlData.conclusie_diagnose')}
                isRequired
                label="Heb je een diagnose?"
            >
                <InputRadioGroup
                    automation="conclusie_diagnose"
                    error={props.errors.get('customControlData.conclusie_diagnose')}
                    name="conclusie_diagnose"
                    onChange={async (value) => await handleChangeHebJeEenDiagnose(value)}
                    onDoValidate={() => props.validateOneCustomControl('conclusie_diagnose')}
                    options={[
                        ConclusieDiagnoseOption.ja,
                        ConclusieDiagnoseOption.nog_geen_diagnose,
                        ConclusieDiagnoseOption.geen_afwijkingen,
                    ]}
                    value={props.diagnosticReport.customControlData.conclusie_diagnose}
                />
            </FormGroup>

            {props.diagnosticReport.customControlData.conclusie_diagnose === ConclusieDiagnoseOption.ja && (
                <FormGroup
                    hasErrors={hasWerkdiagnoseError}
                    isRequired
                    label="Werkdiagnose"
                >
                    <RadioGroup
                        className={`automation-werkdiagnose ${hasWerkdiagnoseError ? 'has-danger' : ''}`}
                        name="werkdiagnose"
                        onBlur={() => props.validateOneCustomControl('werkdiagnose')}
                        onChange={(e) => {
                            const updatedOrthoptischeDiagnosis = { werkdiagnose: e.target.value };
                            props.handleChange('diagnosis', {
                                ...nullDiagnosis,
                                diagnosisConsiderationIcd10:
                                    e.target.value === 'anders' ? null : Icd10Werkdiagnose[e.target.value],
                            });
                            props.handleChangeCustomControl(updatedOrthoptischeDiagnosis);
                        }}
                        value={props.diagnosticReport.customControlData.werkdiagnose}
                    >
                        <Radio
                            label={orthCV1DiagnosticReportWerkdiagnoseLabels.hypermetropie}
                            value="hypermetropie"
                        />
                        <Radio
                            label={orthCV1DiagnosticReportWerkdiagnoseLabels.myopie}
                            value="myopie"
                        />
                        <Radio
                            label={orthCV1DiagnosticReportWerkdiagnoseLabels.anisometropie}
                            value="anisometropie"
                        />
                        <Radio
                            label={orthCV1DiagnosticReportWerkdiagnoseLabels.astigmatisme}
                            value="astigmatisme"
                        />
                        <Radio
                            label={orthCV1DiagnosticReportWerkdiagnoseLabels.insufficientie_en_overmatige_convergentie}
                            value="insufficientie_en_overmatige_convergentie"
                        />
                        <Radio
                            label={orthCV1DiagnosticReportWerkdiagnoseLabels.strabismus}
                            value="strabismus"
                        />
                        <Radio
                            label={orthCV1DiagnosticReportWerkdiagnoseLabels.amblyopie}
                            value="amblyopie"
                        />
                        <Radio
                            label={orthCV1DiagnosticReportWerkdiagnoseLabels.anders}
                            value="anders"
                        />
                    </RadioGroup>

                    {props.diagnosticReport.customControlData.werkdiagnose === 'anders' && (
                        <Icd10Selection
                            icd10Code={props.diagnosticReport.diagnosis.diagnosisConsiderationIcd10}
                            id="working-diagnosis"
                            onChange={async (icd10Code, thesaurusTermId, icd10CodeFallBack) => {
                                if (icd10CodeFallBack) {
                                    await props.handleChange('diagnosis', {
                                        ...props.diagnosticReport.diagnosis,
                                        diagnosisConsiderationIcd10fallback: icd10CodeFallBack,
                                    });
                                } else {
                                    await props.handleChange('diagnosis', {
                                        ...props.diagnosticReport.diagnosis,
                                        diagnosisConsiderationIcd10: icd10Code,
                                        considerationThesaurusTermId: thesaurusTermId,
                                    });
                                }
                            }}
                            onDoValidate={() => {
                                props.validateOne('diagnosis');
                            }}
                            placeholder="Typ of klik en kies je diagnose"
                            specialismCode={props.service.specialismCode || ''}
                            termId={props.diagnosticReport.diagnosis.considerationThesaurusTermId}
                        />
                    )}

                    {hasWerkdiagnoseError && (
                        <Feedback
                            error={
                                props.errors.get('customControlData.werkdiagnose') ??
                                props.errors.get('diagnosis.diagnosisConsiderationIcd10')
                            }
                        />
                    )}
                </FormGroup>
            )}

            {props.diagnosticReport.diagnosis.diagnosisConsiderationIcd10 && (
                <FormGroup
                    hasErrors={
                        props.errors.has('diagnosis.diagnosisDifferentialConsiderationIcd10a') ||
                        props.errors.has('diagnosis.diagnosisDifferentialConsiderationIcd10b') ||
                        props.errors.has('diagnosis.diagnosisDifferentialConsiderationIcd10c')
                    }
                    label="Differentiaal diagnose"
                >
                    <Icd10Selection
                        error={props.errors.get('diagnosis.diagnosisDifferentialConsiderationIcd10a')}
                        icd10Code={props.diagnosticReport.diagnosis.diagnosisDifferentialConsiderationIcd10a}
                        id="differential-diagnosis-0"
                        onChange={(icd10Code, thesaurusTermId) => {
                            props.handleChange('diagnosis', {
                                ...props.diagnosticReport.diagnosis,
                                diagnosisDifferentialConsiderationIcd10a: icd10Code,
                                differentialConsiderationThesaurusTermIda: thesaurusTermId,
                            });
                        }}
                        onDoValidate={() => {
                            props.validateOne('diagnosis');
                        }}
                        placeholder="Typ of klik en kies je diagnose"
                        specialismCode={props.service.specialismCode || ''}
                        termId={props.diagnosticReport.diagnosis.differentialConsiderationThesaurusTermIda}
                    />
                    {props.diagnosticReport.diagnosis.diagnosisDifferentialConsiderationIcd10a && (
                        <>
                            <Icd10Selection
                                error={props.errors.get('diagnosis.diagnosisDifferentialConsiderationIcd10b')}
                                icd10Code={props.diagnosticReport.diagnosis.diagnosisDifferentialConsiderationIcd10b}
                                id="differential-diagnosis-1"
                                onChange={(icd10Code, thesaurusTermId) => {
                                    props.handleChange('diagnosis', {
                                        ...props.diagnosticReport.diagnosis,
                                        diagnosisDifferentialConsiderationIcd10b: icd10Code,
                                        differentialConsiderationThesaurusTermIdb: thesaurusTermId,
                                    });
                                }}
                                onDoValidate={() => {
                                    props.validateOne('diagnosis');
                                }}
                                placeholder="Typ of klik en kies je diagnose"
                                specialismCode={props.service.specialismCode || ''}
                                termId={props.diagnosticReport.diagnosis.differentialConsiderationThesaurusTermIdb}
                            />
                            {props.diagnosticReport.diagnosis.diagnosisDifferentialConsiderationIcd10b && (
                                <>
                                    <Icd10Selection
                                        error={props.errors.get('diagnosis.diagnosisDifferentialConsiderationIcd10c')}
                                        icd10Code={
                                            props.diagnosticReport.diagnosis.diagnosisDifferentialConsiderationIcd10c
                                        }
                                        id="differential-diagnosis-2"
                                        onChange={(icd10Code, thesaurusTermId) => {
                                            props.handleChange('diagnosis', {
                                                ...props.diagnosticReport.diagnosis,
                                                diagnosisDifferentialConsiderationIcd10c: icd10Code,
                                                differentialConsiderationThesaurusTermIdc: thesaurusTermId,
                                            });
                                        }}
                                        onDoValidate={() => {
                                            props.validateOne('diagnosis');
                                        }}
                                        placeholder="Typ of klik en kies je diagnose"
                                        specialismCode={props.service.specialismCode || ''}
                                        termId={
                                            props.diagnosticReport.diagnosis.differentialConsiderationThesaurusTermIdc
                                        }
                                    />
                                </>
                            )}
                        </>
                    )}
                </FormGroup>
            )}

            <Divider />
        </>
    );

    function handleChangeHebJeEenDiagnose(value: string | null) {
        props.resetWerkdiagnose();
        props.handleChangeCustomControl({ conclusie_diagnose: value });
    }
}
